import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Download, 
  Eye, 
  CheckCircle2, 
  Clock, 
  Sparkles, 
  RefreshCw, 
  Layers, 
  AlertCircle,
  X,
  FileCode
} from 'lucide-react';
import { EmptyResumesIllustration, PreviewErrorIllustration } from './Illustrations';
import { API_BASE_URL } from '../config';

export default function MyResumes({ onBackToAnalyzer }) {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewModalUrl, setPreviewModalUrl] = useState(null);
  const [previewResumeName, setPreviewResumeName] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchResumes = async (retry = true) => {
    setLoading(true);
    setError('');
    try {
      let token = localStorage.getItem('access_token');
      let response = await fetch(`${API_BASE_URL}/api/resumes`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        if (response.status === 401) {
          if (retry) {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              try {
                const refreshRes = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ refresh_token: refreshToken })
                });
                if (refreshRes.ok) {
                  const refreshData = await refreshRes.json();
                  if (refreshData.status === 'success' && refreshData.access_token) {
                    localStorage.setItem('access_token', refreshData.access_token);
                    // Retry immediately with new token
                    token = refreshData.access_token;
                    response = await fetch(`${API_BASE_URL}/api/resumes`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                  }
                }
              } catch (refreshErr) {
                console.error("Token refresh failed", refreshErr);
              }
            }
          }
          
          if (!response.ok && response.status === 401) {
            // If still 401, they really need to sign in
            throw new Error('Your session has expired. Please sign in again.');
          }
        }
        
        if (!response.ok) {
          throw new Error('Failed to load your resumes. Please try again.');
        }
      }

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setResumes(data.resumes || []);
        } else {
          throw new Error(data.message || 'Unable to retrieve resumes');
        }
      }
    } catch (err) {
      setError(err.message || 'Error fetching resumes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResumes();
  }, []);

  const handleDownload = async (resume, type = 'pdf') => {
    const actionKey = `${resume.resume_id}-${type}`;
    setDownloadingId(actionKey);
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = `${API_BASE_URL}/api/resumes/${resume.resume_id}/download-${type}`;
      
      const res = await fetch(endpoint, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ detail: `Failed to download ${type.toUpperCase()}` }));
        alert(errData.detail || `Download error for ${type.toUpperCase()}`);
        return;
      }

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const cleanName = (resume.resume_name || 'Tailored_Resume').replace(/\s+/g, '_');
      link.download = `${cleanName}.${type}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
      alert(`Download failed: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = async (resume) => {
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = `${API_BASE_URL}/api/resumes/${resume.resume_id}/preview`;
      
      const res = await fetch(endpoint, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ detail: 'Preview not available' }));
        alert(errData.detail || 'Preview not available');
        return;
      }

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewModalUrl(blobUrl);
      setPreviewResumeName(resume.resume_name || 'Resume Preview');
    } catch (err) {
      alert(`Could not load preview: ${err.message}`);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Recent';
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return 'Recent';
    }
  };

  const resolveTemplateLabel = (templateName) => {
    if (!templateName) return 'Professional Blue';
    if (templateName === 'tpl_1' || templateName.includes('3477a530') || templateName.toLowerCase().includes('professional')) return 'Professional Blue';
    if (templateName === 'tpl_2' || templateName.includes('840ff210') || templateName.toLowerCase().includes('yellow')) return 'Standard Yellow';
    if (templateName === 'tpl_3' || templateName.includes('0278c937') || templateName.toLowerCase().includes('white')) return 'Clean White';
    return templateName;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 16 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.28, ease: 'easeOut' } 
    },
  };

  return (
    <div className="max-w-5xl mx-auto w-full p-3.5 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 pb-3 sm:pb-4 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white">My Resumes</h2>
            <span className="px-2.5 py-0.5 text-[11px] sm:text-xs font-semibold bg-indigo-500/20 text-indigo-300 rounded-full border border-indigo-500/30">
              {resumes.length} saved
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Access, view, and download all your ATS-optimized tailored resumes.
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-between sm:justify-end">
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={fetchResumes}
            disabled={loading}
            className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 transition border border-slate-700 cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </motion.button>

          {onBackToAnalyzer && (
            <motion.button
              whileHover={{ scale: 1.03, boxShadow: '0 8px 20px -4px rgba(99, 102, 241, 0.45)' }}
              whileTap={{ scale: 0.97 }}
              onClick={onBackToAnalyzer}
              className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 px-3.5 sm:px-4 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition shadow-sm cursor-pointer whitespace-nowrap"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Tailor New
            </motion.button>
          )}
        </div>
      </div>

      {/* Error Card / Banner */}
      {error && resumes.length === 0 ? (
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8 text-center space-y-4">
          <PreviewErrorIllustration className="w-32 h-32 mx-auto" />
          <h3 className="text-lg font-bold text-white">Unable to Load Resumes</h3>
          <p className="text-xs sm:text-sm text-slate-400 max-w-md mx-auto">{error}</p>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={fetchResumes}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 font-semibold text-xs rounded-xl text-white transition shadow-md shadow-indigo-600/20"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Try Again
          </motion.button>
        </div>
      ) : error && (
        <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-400 text-sm flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading Skeleton */}
      {loading && resumes.length === 0 && (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 animate-pulse space-y-4"
            >
              <div className="h-5 bg-slate-800 rounded w-1/3"></div>
              <div className="h-4 bg-slate-800/60 rounded w-1/4"></div>
              <div className="h-10 bg-slate-800/40 rounded w-full"></div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State with Custom Illustration */}
      {!loading && resumes.length === 0 && !error && (
        <div className="bg-slate-900/40 border border-slate-800/80 rounded-3xl p-10 sm:p-14 text-center space-y-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/5 via-transparent to-transparent pointer-events-none" />
          
          <EmptyResumesIllustration className="w-44 h-44 sm:w-52 sm:h-52 mx-auto relative z-10" />

          <div className="relative z-10 space-y-2 max-w-md mx-auto">
            <h3 className="text-xl font-bold text-white tracking-tight">No Tailored Resumes Yet</h3>
            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
              Upload your resume and paste your target job description to generate an ATS-optimized, beautifully formatted single-page resume.
            </p>
          </div>

          {onBackToAnalyzer && (
            <div className="pt-2 relative z-10">
              <motion.button
                whileHover={{ scale: 1.03, boxShadow: '0 10px 25px -4px rgba(99, 102, 241, 0.45)' }}
                whileTap={{ scale: 0.97 }}
                onClick={onBackToAnalyzer}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 font-bold text-sm rounded-xl text-white transition shadow-lg shadow-indigo-600/25"
              >
                <Sparkles className="w-4 h-4" />
                Tailor Your First Resume
              </motion.button>
            </div>
          )}
        </div>
      )}

      {/* Resume Cards List with Staggered Entrance */}
      <motion.div 
        variants={containerVariants} 
        initial="hidden" 
        animate="show" 
        className="space-y-4"
      >
        {resumes.map((resume) => {
          const displayScore = resume.ats_score_after || resume.ats_score_before || 90;
          const templateTitle = resolveTemplateLabel(resume.template_name);
          const isPaid = resume.is_paid || resume.resume_status === 'paid' || resume.resume_status === 'completed';

          return (
            <motion.div
              key={resume.resume_id}
              variants={cardVariants}
              whileHover={{ y: -2, transition: { duration: 0.2 } }}
              className="bg-slate-900/80 border border-slate-800 hover:border-slate-700/80 rounded-2xl p-4 sm:p-6 transition-colors duration-200 shadow-sm space-y-3.5 sm:space-y-5"
            >
              {/* Card Top: Title, Score Badge, Status */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base sm:text-lg font-bold text-slate-100">
                      {resume.resume_name || 'Tailored Resume'}
                    </h3>
                    {isPaid && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        <CheckCircle2 className="w-3 h-3" />
                        Paid
                      </span>
                    )}
                    {resume.design_upgraded && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                        <Sparkles className="w-3 h-3" />
                        Design Upgraded
                      </span>
                    )}
                  </div>
                  {resume.original_filename && (
                    <p className="text-[11px] sm:text-xs text-slate-400 truncate max-w-xs sm:max-w-md">
                      Source: <span className="font-mono text-slate-300">{resume.original_filename}</span>
                    </p>
                  )}
                </div>

                {/* ATS Score Display */}
                <div className="flex items-center gap-3 sm:gap-4 bg-slate-950/60 border border-slate-800/80 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl self-start sm:self-auto">
                  <div className="text-left sm:text-right">
                    <p className="text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-400 font-semibold">ATS Score</p>
                    <p className="text-lg sm:text-xl font-black text-emerald-400">
                      {displayScore}
                      <span className="text-xs text-slate-500 font-normal"> / 100</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Card Meta Details */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 text-xs text-slate-300 bg-slate-950/40 p-3 sm:p-3.5 rounded-xl border border-slate-800/50">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                  <div>
                    <span className="text-slate-500 block text-[10px] uppercase">Template</span>
                    <span className="font-medium text-slate-200">{templateTitle}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <div>
                    <span className="text-slate-500 block text-[10px] uppercase">Created</span>
                    <span className="font-medium text-slate-200">{formatDate(resume.created_at)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <div>
                    <span className="text-slate-500 block text-[10px] uppercase">Status</span>
                    <span className="font-medium text-emerald-400 capitalize">
                      {resume.resume_status || 'Ready'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons: [View] [Download PDF] [Download DOCX] */}
              <div className="flex items-center gap-2 pt-1 flex-wrap w-full">
                {/* [View] */}
                <motion.button
                  whileHover={{ scale: 1.025 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => handleView(resume)}
                  className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 px-3.5 sm:px-4 py-2 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition cursor-pointer"
                  title="Preview PDF"
                >
                  <Eye className="w-3.5 h-3.5 text-indigo-400" />
                  View
                </motion.button>

                {/* [Download PDF] */}
                <motion.button
                  whileHover={{ scale: 1.025, boxShadow: '0 8px 20px -4px rgba(99, 102, 241, 0.45)' }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => handleDownload(resume, 'pdf')}
                  disabled={downloadingId === `${resume.resume_id}-pdf`}
                  className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 px-3.5 sm:px-4 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition shadow-sm cursor-pointer whitespace-nowrap"
                  title="Download Tailored PDF"
                >
                  <Download className="w-3.5 h-3.5" />
                  {downloadingId === `${resume.resume_id}-pdf` ? 'Downloading...' : 'Download PDF'}
                </motion.button>

                {/* [Download DOCX] */}
                <motion.button
                  whileHover={{ scale: 1.025 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => handleDownload(resume, 'docx')}
                  disabled={downloadingId === `${resume.resume_id}-docx`}
                  className="flex-1 sm:flex-none justify-center flex items-center gap-1.5 px-3.5 sm:px-4 py-2 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition cursor-pointer whitespace-nowrap"
                  title="Download Editable DOCX"
                >
                  <FileCode className="w-3.5 h-3.5 text-blue-400" />
                  {downloadingId === `${resume.resume_id}-docx` ? 'Downloading...' : 'Download DOCX'}
                </motion.button>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* PDF Preview Modal with Backdrop Blur & Scale/Slide Animation */}
      <AnimatePresence>
        {previewModalUrl && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-2 sm:p-4"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 15 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-4xl max-h-[94vh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            >
              {/* Modal Header */}
              <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-400" />
                  <h3 className="font-bold text-sm text-slate-200">{previewResumeName}</h3>
                </div>
                <button
                  onClick={() => {
                    window.URL.revokeObjectURL(previewModalUrl);
                    setPreviewModalUrl(null);
                  }}
                  className="text-slate-400 hover:text-slate-100 p-1 rounded-lg hover:bg-slate-800 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Embedded PDF Viewer */}
              <div className="flex-1 bg-slate-950 p-2 overflow-auto">
                <iframe
                  src={previewModalUrl}
                  title="Resume Preview"
                  className="w-full h-[70vh] rounded-lg border border-slate-800"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
